import type { TicketStatus } from './enums.js'

/**
 * Regras do funil do chamado (ADR-0020) — o espelho, na tela, do que
 * `sistemas.change_ticket_status` impõe no banco. A tela usa para mostrar só
 * os botões que o banco vai aceitar; quem manda continua sendo o banco.
 */

/** Toda troca de status tem uma explicação deste tamanho, que vai para quem abriu. */
export const TICKET_STATUS_MESSAGE_MIN = 50

export const TICKET_TRANSITIONS: Record<TicketStatus, readonly TicketStatus[]> = {
  aberto: ['em_andamento', 'cancelado'],
  em_andamento: ['pausado', 'resolvido', 'cancelado'],
  pausado: ['em_andamento', 'resolvido', 'cancelado'],
  resolvido: ['em_andamento'],
  cancelado: [],
}

export function canTransition(from: TicketStatus, to: TicketStatus): boolean {
  return TICKET_TRANSITIONS[from].includes(to)
}

/** O verbo do botão: o que a pessoa FAZ, não o nome do status. */
export const TICKET_TRANSITION_ACTION: Record<TicketStatus, string> = {
  aberto: 'Voltar para novo',
  em_andamento: 'Atender',
  pausado: 'Aguardar solicitante',
  resolvido: 'Resolver',
  cancelado: 'Cancelar',
}

/** Quando o destino é "em atendimento" vindo de resolvido, o verbo é reabrir. */
export function transitionAction(from: TicketStatus, to: TicketStatus): string {
  if (from === 'resolvido' && to === 'em_andamento') return 'Reabrir'
  if (from === 'pausado' && to === 'em_andamento') return 'Retomar'
  return TICKET_TRANSITION_ACTION[to]
}

/** O rótulo que o SOLICITANTE lê no WhatsApp (`ticket_status_public_label`). */
export const TICKET_STATUS_PUBLIC_LABEL: Record<TicketStatus, string> = {
  aberto: 'Recebido',
  em_andamento: 'Em atendimento',
  pausado: 'Aguardando você',
  resolvido: 'Resolvido',
  cancelado: 'Cancelado',
}

export interface ResolveBlock {
  readonly ok: boolean
  readonly reason?: string
}

/** Resolvido só com pelo menos uma atividade e todas concluídas. */
export function canResolve(activities: readonly { readonly completedAt?: string | undefined }[]): ResolveBlock {
  if (activities.length === 0) return { ok: false, reason: 'Todo chamado vira atividade: gere pelo menos uma antes de resolver.' }
  const open = activities.filter((a) => !a.completedAt).length
  if (open > 0) return { ok: false, reason: `Falta concluir ${open} de ${activities.length} atividade${activities.length === 1 ? '' : 's'}.` }
  return { ok: true }
}

/**
 * Motivo do chamado (vai em `ticket.category`). O formulário mostra a lista
 * para tocar; o banco confere a chave (`public_open_ticket`). "Novo sistema"
 * não aparece na lista: é o que a escolha "Novo sistema" grava sozinha.
 */
export const TICKET_REASONS = [
  { key: 'erro', label: 'Erro ou algo não funciona', hint: 'Tela travada, mensagem de erro, dado errado' },
  { key: 'duvida', label: 'Dúvida de uso', hint: 'Como faço tal coisa no sistema' },
  { key: 'acesso', label: 'Acesso ou senha', hint: 'Não consigo entrar, preciso de permissão' },
  { key: 'melhoria', label: 'Pedido de melhoria', hint: 'Algo novo ou diferente num sistema que existe' },
  { key: 'outro', label: 'Outro', hint: 'Nenhum dos anteriores' },
] as const

export type TicketReason = (typeof TICKET_REASONS)[number]['key'] | 'novo_sistema'

const REASON_LABEL: Record<string, string> = {
  ...Object.fromEntries(TICKET_REASONS.map((r) => [r.key, r.label])),
  novo_sistema: 'Pedido de sistema novo',
}

/** Rótulo do motivo; chamados antigos guardam texto livre e aparecem como estão. */
export function ticketReasonLabel(category: string | null | undefined): string | null {
  const value = (category ?? '').trim()
  if (!value) return null
  return REASON_LABEL[value] ?? value
}
